import { isThisMonth } from 'date-fns';
import { UsageType, UsageTypeValue } from 'src/common/components/UsageTypeSwitch/UsageTypeSwitch';
import { DatasetPoint } from 'src/common/components/Graphs/common/Graphs/BarChart';
import { getXAxisMonthLabels } from 'src/common/utils/chartAxisLabelsUtils';
import { MonthlyUsageDto } from '../components/Sim/Details/api/detailsUsageApi';
import { TFunction } from 'i18next';

const BAR_BLUE_COLOR = '#6fb7e1';
const BAR_BLUE_HIGHLIGHT_COLOR = '#55bcf0';
const BORDER_BLUE_COLOR = '#3791c5';

const BORDER_DARK_BLUE_COLOR = '#597b82';

const BAR_GREEN_COLOR = '#96db9d';
const BAR_GREEN_HIGHLIGHT_COLOR = '#6dc976';
const BORDER_GREEN_COLOR = '#4b9256';

const checkForAllZeros = (data: DatasetPoint[]) =>
  data.every((item) => Number.parseFloat(item.value) === 0);

const getTotalDatasetBarColor = (currentMonth: boolean, MTAndMOExist: boolean) => {
  if (currentMonth) {
    return BAR_BLUE_HIGHLIGHT_COLOR;
  }

  return MTAndMOExist ? BORDER_DARK_BLUE_COLOR : BAR_BLUE_COLOR;
};

export const mapMonthlyUsageDTOtoBarChartData = (
  data: MonthlyUsageDto,
  usageType: UsageTypeValue,
  t: TFunction,
) => {
  const yAxisLabels = data?.unit?.toLowerCase() !== 'none' ? data.unit : '';

  if (!data?.data?.length) {
    return {
      datasets: [],
      xAxisLabels: [],
      yAxisLabel: yAxisLabels,
    };
  }

  const isDataType = usageType === UsageType.Data;

  const { totalDataset, moDataset, mtDataset } = data.data.reduce(
    (acc, dataItem) => {
      const sumOfMTandMO =
        Number.parseFloat(dataItem.value.mo) + Number.parseFloat(dataItem.value.mt);
      const MTAndMOExist = sumOfMTandMO > 0;
      const currentMonth = isThisMonth(new Date(dataItem.label));

      acc.moDataset.push({
        label: dataItem.label,
        value: dataItem.value.mo ?? 0,
        barColor: currentMonth ? BAR_GREEN_HIGHLIGHT_COLOR : BAR_GREEN_COLOR,
        borderColor: BORDER_GREEN_COLOR,
      });

      acc.mtDataset.push({
        label: dataItem.label,
        value: dataItem.value.mt ?? 0,
        barColor: currentMonth ? BAR_BLUE_HIGHLIGHT_COLOR : BAR_BLUE_COLOR,
        borderColor: BORDER_BLUE_COLOR,
      });

      acc.totalDataset.push({
        label: dataItem.label,
        value: MTAndMOExist ? sumOfMTandMO.toFixed(3).toString() : dataItem.value.total,
        barColor: getTotalDatasetBarColor(currentMonth, MTAndMOExist),
        borderColor: MTAndMOExist ? BORDER_DARK_BLUE_COLOR : BORDER_BLUE_COLOR,
      });

      return acc;
    },
    {
      totalDataset: [] as DatasetPoint[],
      moDataset: [] as DatasetPoint[],
      mtDataset: [] as DatasetPoint[],
    },
  );

  const datasets = [];

  const moExist = !!moDataset.length && !checkForAllZeros(moDataset);
  const mtExist = !!mtDataset.length && !checkForAllZeros(mtDataset);

  datasets.push(
    {
      label: isDataType ? t('common.upload') : 'MO',
      data: moDataset,
      hidden: !moExist,
    },
    {
      label: isDataType ? t('common.download') : 'MT',
      data: mtDataset,
      hidden: !mtExist,
    },
    {
      label: t('common.total'),
      data: totalDataset,
      hidden: moExist || mtExist,
    },
  );

  const xAxisLabels = getXAxisMonthLabels(data.data);

  return {
    datasets,
    xAxisLabels,
    yAxisLabel: yAxisLabels,
  };
};
